import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Shield, Download, Search, Filter } from "lucide-react";
import { useGaContext } from "@/hooks/useGaContext";

const ACTION_TYPES = [
  "viewed_client",
  "edited_employee",
  "submitted_rfp",
  "created_employer_group",
  "viewed_broker",
  "assigned_license",
  "revoked_license",
  "granted_build_access",
  "revoked_build_access",
];

function exportToCsv(rows: any[]) {
  const headers = ["Timestamp", "GA User", "Action", "Broker", "Client", "Record Type", "Record ID"];
  const lines = [
    headers.join(","),
    ...rows.map((r) =>
      [
        r.createdAt ? new Date(r.createdAt).toISOString() : "",
        r.gaUserName || r.gaUserId || "",
        r.action || "",
        r.brokerAccountId || "",
        r.clientId || "",
        r.recordType || "",
        r.recordId || "",
      ]
        .map((v) => `"${String(v).replace(/"/g, '""')}"`)
        .join(",")
    ),
  ];
  const blob = new Blob([lines.join("\n")], { type: "text/csv" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `ga-audit-log-${new Date().toISOString().slice(0, 10)}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

export default function GAAuditLog() {
  const { gaId } = useGaContext();
  const [search, setSearch] = useState("");
  const [filterAction, setFilterAction] = useState("all");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  const params = new URLSearchParams();
  if (filterAction && filterAction !== "all") params.set("action", filterAction);
  if (dateFrom) params.set("date_from", dateFrom);
  if (dateTo) params.set("date_to", dateTo);

  const { data: logs = [], isLoading } = useQuery<any[]>({
    queryKey: ["/api/ga", gaId, "audit-log", filterAction, dateFrom, dateTo],
    queryFn: async () => {
      const res = await fetch(`/api/ga/${gaId}/audit-log?${params.toString()}`);
      if (!res.ok) return [];
      return res.json();
    },
    enabled: !!gaId,
  });

  const filtered = logs.filter((l) => {
    if (!search) return true;
    return (
      (l.action || "").toLowerCase().includes(search.toLowerCase()) ||
      (l.gaUserName || l.gaUserId || "").toLowerCase().includes(search.toLowerCase()) ||
      (l.brokerAccountId || "").toLowerCase().includes(search.toLowerCase())
    );
  });

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-foreground">Audit Log</h1>
          <p className="text-sm text-muted-foreground mt-0.5">Immutable record of all GA actions</p>
        </div>
        <Button variant="outline" onClick={() => exportToCsv(filtered)}>
          <Download className="w-4 h-4 mr-2" /> Export CSV
        </Button>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 min-w-52">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            className="pl-9"
            placeholder="Search actions, users, brokers..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <Select value={filterAction} onValueChange={setFilterAction}>
          <SelectTrigger className="w-48">
            <SelectValue placeholder="Action type" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Actions</SelectItem>
            {ACTION_TYPES.map((a) => (
              <SelectItem key={a} value={a}>{a.replace(/_/g, " ")}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <div className="flex items-center gap-2">
          <Input
            type="date"
            className="w-36 text-xs"
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
            placeholder="From"
          />
          <span className="text-muted-foreground text-sm">—</span>
          <Input
            type="date"
            className="w-36 text-xs"
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
            placeholder="To"
          />
        </div>
      </div>

      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="flex items-center justify-center py-16">
              <div className="animate-spin w-5 h-5 border-2 border-primary border-t-transparent rounded-full" />
            </div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-16 text-muted-foreground">
              <Shield className="w-10 h-10 mx-auto mb-3 opacity-40" />
              <p className="text-sm">No audit entries found</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-muted border-b border-border">
                  <tr className="text-xs text-muted-foreground uppercase tracking-wide">
                    <th className="text-left px-4 py-3">Timestamp</th>
                    <th className="text-left px-4 py-3">GA User</th>
                    <th className="text-left px-4 py-3">Action</th>
                    <th className="text-left px-4 py-3">Broker</th>
                    <th className="text-left px-4 py-3">Client</th>
                    <th className="text-left px-4 py-3">Record</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {filtered.map((entry: any) => (
                    <tr key={entry.id} className="hover:bg-muted/50">
                      <td className="px-4 py-3 text-xs text-muted-foreground whitespace-nowrap">
                        {entry.createdAt ? new Date(entry.createdAt).toLocaleString() : "—"}
                      </td>
                      <td className="px-4 py-3 text-foreground">
                        {entry.gaUserName || entry.gaUserId || "—"}
                      </td>
                      <td className="px-4 py-3">
                        <span className="inline-flex px-2 py-0.5 rounded text-xs font-medium bg-muted text-muted-foreground">
                          {entry.action}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-muted-foreground text-xs">{entry.brokerAccountId || "—"}</td>
                      <td className="px-4 py-3 text-muted-foreground text-xs">{entry.clientId || "—"}</td>
                      <td className="px-4 py-3 text-muted-foreground text-xs">
                        {entry.recordType ? `${entry.recordType} · ${entry.recordId || ""}` : "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
